// src/utils/exportUtils.js
import * as XLSX from "sheetjs-style";

/**
 * Builds the array of styled cell objects for a single table column.
 * This helper function keeps the main logic cleaner.
 * @param {object} table - The table object.
 * @returns {Array} An array of cell objects for one column.
 */
const buildTableColumn = (table) => {
  const columnCells = [];

  // Row 1: The table's number
  columnCells.push({
    v: `Table ${table.tableNumber}`,
    s: {
      font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4F81BD" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "4F81BD" } },
        bottom: { style: "thin", color: { rgb: "4F81BD" } },
        left: { style: "thin", color: { rgb: "4F81BD" } },
        right: { style: "thin", color: { rgb: "4F81BD" } },
      },
    },
  });

  // Row 2: The table's name
  columnCells.push({
    v: table.name,
    s: {
      font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "8DB4E2" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "4F81BD" } },
        bottom: { style: "thin", color: { rgb: "4F81BD" } },
        left: { style: "thin", color: { rgb: "4F81BD" } },
        right: { style: "thin", color: { rgb: "4F81BD" } },
      },
    },
  });

  // Row 3: A summary of total pax
  const totalPax = table.guests.reduce((sum, guest) => sum + guest.pax, 0);
  const babyPax = table.guests.reduce(
    (sum, guest) => sum + guest.babyChairCount,
    0
  );
  let totalBabyPaxIfAvailable = "";
  if (babyPax > 0) {
    totalBabyPaxIfAvailable = `(${babyPax} 👶)`;
  }
  columnCells.push({
    v: `Total: ${totalPax} pax ${totalBabyPaxIfAvailable}`,
    s: {
      font: { bold: false, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "C5D9F1" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "4F81BD" } },
        bottom: { style: "thin", color: { rgb: "4F81BD" } },
        left: { style: "thin", color: { rgb: "4F81BD" } },
        right: { style: "thin", color: { rgb: "4F81BD" } },
      },
    },
  });

  // Rows 4 onwards: The list of guest names
  table.guests.forEach((guest) => {
    let babyPaxIfAvailable = "";
    if (guest.babyChairCount > 0) {
      babyPaxIfAvailable = `(${guest.babyChairCount} 👶)`;
    }

    columnCells.push({
      v: `${guest.guestName} (${guest.pax} pax) ${babyPaxIfAvailable}`,
      s: {
        fill: { fgColor: { rgb: "DAEEF3" } },
        border: {
          top: { style: "thin", color: { rgb: "B0B0B0" } },
          bottom: { style: "thin", color: { rgb: "B0B0B0" } },
          left: { style: "thin", color: { rgb: "4F81BD" } },
          right: { style: "thin", color: { rgb: "4F81BD" } },
        },
      },
    });
  });

  return columnCells;
};

// Vertical Export (10 in a row)
export const exportTablePlanningToExcel = (tables) => {
  // 1. Define the layout parameters
  const tablesPerRow = 10;
  const rowSpacing = 2;
  const ws = {}; // The worksheet object we will build manually
  let startRow = 0;
  let maxCols = 0;

  // 2. Process tables in chunks (e.g., chunks of 10)
  for (let i = 0; i < tables.length; i += tablesPerRow) {
    const chunk = tables.slice(i, i + tablesPerRow);
    let chunkMaxHeight = 0;

    // 3. For each table in the chunk, build its column and place it in the worksheet
    chunk.forEach((table, chunkIndex) => {
      const columnData = buildTableColumn(table);
      chunkMaxHeight = Math.max(chunkMaxHeight, columnData.length);
      maxCols = Math.max(maxCols, chunkIndex + 1);

      // Place each cell of the column into the worksheet at the correct row/col
      columnData.forEach((cellData, rowIndex) => {
        const cellAddress = XLSX.utils.encode_cell({
          r: startRow + rowIndex, // The current row offset + the cell's row index
          c: chunkIndex, // The column index within the chunk (0-9)
        });
        ws[cellAddress] = cellData;
      });
    });

    // 4. Update the starting row for the next chunk of tables
    startRow += chunkMaxHeight + rowSpacing;
  }

  // 5. Set the final worksheet range and column widths
  const lastRow = startRow > 0 ? startRow - rowSpacing - 1 : 0;
  ws["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: lastRow, c: Math.max(0, maxCols - 1) },
  });
  ws["!cols"] = Array(maxCols).fill({ wch: 35 });

  // 6. Create workbook and download
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Table Layout");
  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Table_Layout_${today}.xlsx`);
};

export const exportTablePlanningToExcelOld = (tables, unseatedGuests) => {
  // 1. Prepare data for the "Seated Guests" sheet
  const seatedGuestsData = tables.flatMap((table) =>
    table.guests.map((guest) => ({
      "Table Number": table.tableNumber,
      "Table Name": table.name,
      "Guest Name": guest.guestName,
      Pax: guest.pax,
      "Invited By": guest.invitedBy,
      Vegetarian: guest.isVegetarian ? "Yes" : "No",
      "Baby Chairs": guest.babyChairCount || 0,
    }))
  );

  // 2. Prepare data for the "Unseated Guests" sheet
  const unseatedGuestsData = unseatedGuests.map((guest) => ({
    "Guest Name": guest.guestName,
    Pax: guest.pax,
    "Invited By": guest.invitedBy,
    Vegetarian: guest.isVegetarian ? "Yes" : "No",
    "Baby Chairs": guest.babyChairCount || 0,
  }));

  // 3. Prepare data for the "Tables Summary" sheet
  const tablesSummaryData = tables.map((table) => {
    const currentPax = table.guests.reduce((sum, g) => sum + g.pax, 0);
    return {
      "Table Number": table.tableNumber,
      "Table Name": table.name,
      Capacity: table.capacity,
      "Guests Seated (Pax)": currentPax,
      "Remaining Capacity": table.capacity - currentPax,
    };
  });

  // 4. Create worksheets from the data
  const wsSeated = XLSX.utils.json_to_sheet(seatedGuestsData);
  const wsUnseated = XLSX.utils.json_to_sheet(unseatedGuestsData);
  const wsSummary = XLSX.utils.json_to_sheet(tablesSummaryData);

  // Optional: Set column widths for better readability
  wsSeated["!cols"] = [
    { wch: 15 },
    { wch: 20 },
    { wch: 25 },
    { wch: 5 },
    { wch: 12 },
    { wch: 10 },
    { wch: 12 },
  ];
  wsUnseated["!cols"] = [
    { wch: 25 },
    { wch: 5 },
    { wch: 12 },
    { wch: 10 },
    { wch: 12 },
  ];
  wsSummary["!cols"] = [
    { wch: 15 },
    { wch: 20 },
    { wch: 10 },
    { wch: 20 },
    { wch: 20 },
  ];

  // 5. Create a new workbook and add the worksheets
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsSeated, "Seated Guests by Table");
  XLSX.utils.book_append_sheet(wb, wsSummary, "Tables Summary");
  XLSX.utils.book_append_sheet(wb, wsUnseated, "Unseated Guests");

  // 6. Generate the Excel file and trigger a download
  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Table_Plan_${today}.xlsx`);
};
